import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

interface AdminRouteProps {
  children: React.ReactNode;
}

/**
 * Client-side admin guard. Server-side enforcement (RLS + edge-function role
 * checks) is the source of truth — this is a UX layer to keep non-admins out
 * of admin pages.
 */
export const AdminRoute = ({ children }: AdminRouteProps) => {
  const [status, setStatus] = useState<"loading" | "admin" | "denied">("loading");

  useEffect(() => {
    let cancelled = false;

    const verify = async (userId: string | undefined) => {
      if (!userId) {
        if (!cancelled) setStatus("denied");
        return;
      }
      const { data, error } = await supabase.rpc("has_role", {
        _user_id: userId,
        _role: "admin",
      });
      if (cancelled) return;
      setStatus(!error && data === true ? "admin" : "denied");
    };

    // onAuthStateChange fires INITIAL_SESSION synchronously after subscribe,
 // which avoids the global auth lock that getSession() can deadlock on.
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      verify(session?.user?.id);
    });

    // Safety net: if no event fires within 4s, treat as unauthenticated.
    const timeout = setTimeout(() => {
      if (!cancelled) setStatus((s) => (s === "loading" ? "denied" : s));
    }, 4000);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
      sub.subscription.unsubscribe();
    };
  }, []);

  if (status === "loading") {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (status === "denied") {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
};

export default AdminRoute;